import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ShieldCheckIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { useWalletStore } from '../store/walletStore';
import { createTokenOps, assertSymbolAvailable } from '../api/vexforge';
import { signAndSend, ensureConnected, EXPLORER_TX_URL } from '../lib/submit';

const gradeColors = {
  A: 'bg-green-500/20 text-green-400 border-green-500/30',
  B: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  C: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  D: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  F: 'bg-red-500/20 text-red-400 border-red-500/30',
};

export default function CreateToken() {
  const navigate = useNavigate();
  const { address, isConnected } = useWalletStore();
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState(null); // { txHash, mintAddress, hasMeta }

  const [formData, setFormData] = useState({
    name: '',
    symbol: '',
    decimals: '9',
    totalSupply: '',
    description: '',
    metadataUri: '',
    website: '',
    twitter: '',
    telegram: '',
    discord: '',
    // Features
    burnable: true,
    mintable: false,
    pausable: false,
    lockMetadata: false,
    // Protection
    maxWalletPercent: '2',
    maxTxPercent: '1',
    liquidityLockDays: '365',
    devVestingMonths: '0',
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const calculateCost = () => {
    let cost = 0.1;
    if (formData.pausable) cost += 0.05;
    return cost.toFixed(2);
  };

  const calculateAntiRugScore = () => {
    let score = 0;
    if (!formData.mintable) score += 25;
    if (parseInt(formData.liquidityLockDays) > 0) score += 25;
    if (parseInt(formData.maxWalletPercent) <= 5) score += 25;
    if (parseInt(formData.devVestingMonths) > 0) score += 25;
    return score;
  };

  const calculateReputationPreview = () => {
    // Factor 1: Mint Authority (max 20)
    const mintAuthority = {
      points: formData.mintable ? 0 : 20,
      max_points: 20,
      reason: formData.mintable ? 'Token is mintable' : 'Mint authority renounced',
    };

    // Factor 2: Liquidity Lock (max 20)
    const lockDays = parseInt(formData.liquidityLockDays) || 0;
    let lpPoints = 0;
    let lpReason = 'No LP lock configured';
    if (lockDays >= 365) { lpPoints = 20; lpReason = `LP locked for ${lockDays} days`; }
    else if (lockDays >= 180) { lpPoints = 16; lpReason = `LP locked for ${lockDays} days`; }
    else if (lockDays >= 90) { lpPoints = 12; lpReason = `LP locked for ${lockDays} days`; }
    else if (lockDays >= 30) { lpPoints = 8; lpReason = `LP locked for ${lockDays} days`; }
    else if (lockDays > 0) { lpPoints = 4; lpReason = `LP locked for ${lockDays} days`; }
    const liquidityLock = { points: lpPoints, max_points: 20, reason: lpReason };

    // Factor 3: Supply Stability (max 10) — always 10 at creation
    const supplyStability = { points: 10, max_points: 10, reason: 'Supply data present' };

    // Factor 4: Creator History (max 20) — default 5 for new creator
    const creatorHistory = { points: 5, max_points: 20, reason: 'First token (updates with on-chain history)' };

    // Factor 5: Presale Status (max 10) — default 5 for direct launch
    const presaleStatus = { points: 5, max_points: 10, reason: 'No presale (direct launch)' };

    // Factor 6: Token Age (max 10) — always 1 for brand new
    const tokenAge = { points: 1, max_points: 10, reason: '0 days old (brand new)' };

    // Factor 7: Metadata Integrity (max 10)
    let metaPoints = 0;
    let metaReason = 'Incomplete metadata';
    const hasDesc = formData.description.trim().length > 0;
    const hasSocial = formData.website || formData.twitter || formData.telegram || formData.discord;
    if (formData.lockMetadata) {
      metaPoints = 10;
      metaReason = 'Metadata permanently locked';
    } else if (hasDesc && hasSocial) {
      metaPoints = 5;
      metaReason = 'Metadata present but mutable';
    }
    const metadataIntegrity = { points: metaPoints, max_points: 10, reason: metaReason };

    const factors = {
      mint_authority: mintAuthority,
      liquidity_lock: liquidityLock,
      supply_stability: supplyStability,
      creator_history: creatorHistory,
      presale_status: presaleStatus,
      token_age: tokenAge,
      metadata_integrity: metadataIntegrity,
    };

    const total = Object.values(factors).reduce((sum, f) => sum + f.points, 0);
    let grade;
    if (total >= 90) grade = 'A';
    else if (total >= 75) grade = 'B';
    else if (total >= 55) grade = 'C';
    else if (total >= 35) grade = 'D';
    else grade = 'F';

    return { score: total, grade, factors };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    const loadingToast = toast.loading('Preparing token...');

    try {
      if (!isConnected) {
        toast.dismiss(loadingToast);
        await ensureConnected();
        return;
      }
      await assertSymbolAvailable(formData.symbol);
      const { ops, mintAddress } = createTokenOps({
        name: formData.name,
        symbol: formData.symbol,
        decimals: formData.decimals,
        wholeSupply: formData.totalSupply,
        creator: address,
      });

      toast.loading('Approve the token creation in VexSpark...', { id: loadingToast });
      const txHash = await signAndSend(
        ops,
        `Create token ${formData.symbol} (${formData.name}) — ${formData.totalSupply} tokens, ${formData.decimals} decimals`,
      );
      if (!txHash) { toast.dismiss(loadingToast); return; }

      toast.success('Token creation submitted!', { id: loadingToast });
      // Metadata (description/links) and the metadata lock are NOT sent in the same
      // signature: they need the token to exist on-chain first. The user applies them
      // from the Edit Token page once the creation is confirmed.
      const hasMeta = !!(formData.description || formData.metadataUri || formData.website ||
                      formData.twitter || formData.telegram || formData.discord || formData.lockMetadata);
      setCreated({ txHash, mintAddress, hasMeta });
    } catch (error) {
      console.error('Error creating token:', error);
      toast.error(error.message || 'Failed to create token', { id: loadingToast });
    } finally {
      setLoading(false);
    }
  };

  const antiRugScore = calculateAntiRugScore();
  const reputationPreview = calculateReputationPreview();
  const cost = calculateCost();

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Create Your Token</h1>
        <p className="text-gray-400">Launch a professional token on Vextaris with built-in anti-rug protection</p>
      </div>

      {created && (
        <div className="mb-6 bg-green-500/10 border border-green-500/30 rounded-2xl p-5 space-y-2">
          <p className="text-green-400 font-medium">Token creation submitted — signed by your wallet.</p>
          <p className="text-sm text-gray-300">
            Transaction:{' '}
            <a href={`${EXPLORER_TX_URL}${created.txHash}`} target="_blank" rel="noopener noreferrer"
              className="font-mono text-vextaris-blue hover:text-white break-all">{created.txHash}</a>
          </p>
          <p className="text-sm text-gray-300">
            Mint address: <span className="font-mono break-all">{created.mintAddress}</span>
            <span className="text-gray-500"> (live once the transaction is included in a block)</span>
          </p>
          {created.hasMeta && (
            <p className="text-sm text-yellow-400">
              Description, links and the metadata lock are applied separately: once the token is
              confirmed, open it and use Edit Token.
            </p>
          )}
          <button type="button" onClick={() => navigate(`/token/${created.mintAddress}`)}
            className="text-sm text-vextaris-blue hover:text-white">View token →</button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Token Information */}
        <div className="bg-vextaris-dark/50 backdrop-blur-xl rounded-2xl border border-vextaris-purple/20 p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Token Information</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Token Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                maxLength={64}
                placeholder="My Awesome Token"
                className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Symbol
              </label>
              <input
                type="text"
                name="symbol"
                value={formData.symbol}
                onChange={handleChange}
                required
                maxLength={10}
                placeholder="MAT"
                className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple uppercase"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Decimals
              </label>
              <input
                type="number"
                name="decimals"
                value={formData.decimals}
                onChange={handleChange}
                required
                min="0"
                max="18"
                className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white focus:outline-none focus:border-vextaris-purple"
              />
              <p className="text-xs text-gray-500 mt-1">Default: 9 (matches VXS)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Total Supply
              </label>
              <input
                type="text"
                name="totalSupply"
                value={formData.totalSupply}
                onChange={handleChange}
                required
                placeholder="1000000"
                className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              placeholder="Describe your token..."
              className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple"
            />
          </div>
        </div>

        {/* Metadata URI */}
        <div className="bg-vextaris-dark/50 backdrop-blur-xl rounded-2xl border border-vextaris-purple/20 p-6">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center space-x-2">
            <LinkIcon className="w-5 h-5 text-vextaris-blue" />
            <span>Token Metadata</span>
          </h2>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Metadata URI (IPFS or HTTP)
            </label>
            <input
              type="text"
              name="metadataUri"
              value={formData.metadataUri}
              onChange={handleChange}
              placeholder="ipfs://Qm... or https://metadata.example.com/token.json"
              className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              JSON file with name, description, image, and attributes. Can be updated later.
            </p>
          </div>
        </div>

        {/* Social Links */}
        <div className="bg-vextaris-dark/50 backdrop-blur-xl rounded-2xl border border-vextaris-purple/20 p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Social Links</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Website</label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://mytoken.com"
                className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Twitter</label>
              <input
                type="url"
                name="twitter"
                value={formData.twitter}
                onChange={handleChange}
                placeholder="https://twitter.com/mytoken"
                className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Telegram</label>
              <input
                type="url"
                name="telegram"
                value={formData.telegram}
                onChange={handleChange}
                placeholder="https://t.me/mytoken"
                className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Discord</label>
              <input
                type="url"
                name="discord"
                value={formData.discord}
                onChange={handleChange}
                placeholder="https://discord.gg/mytoken"
                className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple"
              />
            </div>
          </div>
        </div>

        {/* Token Features */}
        <div className="bg-vextaris-dark/50 backdrop-blur-xl rounded-2xl border border-vextaris-purple/20 p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Token Features</h2>

          <div className="space-y-3">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                name="burnable"
                checked={formData.burnable}
                onChange={handleChange}
                className="w-5 h-5 rounded border-vextaris-purple/30 bg-vextaris-darker text-vextaris-purple focus:ring-vextaris-purple"
              />
              <span className="text-gray-300">Burnable (tokens can be destroyed)</span>
            </label>

            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                name="mintable"
                checked={formData.mintable}
                onChange={handleChange}
                className="w-5 h-5 rounded border-vextaris-purple/30 bg-vextaris-darker text-vextaris-purple focus:ring-vextaris-purple"
              />
              <span className="text-gray-300">Mintable (create new tokens later)</span>
              <ExclamationTriangleIcon className="w-4 h-4 text-yellow-500" />
              <span className="text-xs text-yellow-500">Not recommended for security</span>
            </label>

            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                name="pausable"
                checked={formData.pausable}
                onChange={handleChange}
                className="w-5 h-5 rounded border-vextaris-purple/30 bg-vextaris-darker text-vextaris-purple focus:ring-vextaris-purple"
              />
              <span className="text-gray-300">Pausable (can pause transfers)</span>
            </label>

            <div className="border-t border-vextaris-purple/10 pt-3 mt-3">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="lockMetadata"
                  checked={formData.lockMetadata}
                  onChange={handleChange}
                  className="w-5 h-5 rounded border-vextaris-purple/30 bg-vextaris-darker text-vextaris-purple focus:ring-vextaris-purple"
                />
                <LockClosedIcon className="w-4 h-4 text-green-400" />
                <span className="text-gray-300">Lock Metadata Permanently</span>
                <span className="text-xs text-green-400 font-medium">+10 reputation</span>
              </label>
              {formData.lockMetadata && (
                <p className="text-xs text-yellow-400 mt-2 ml-8">
                  Metadata will be permanently locked after creation. You will not be able to update the description, image, or social links. Fill in all metadata fields above before enabling this.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Anti-Rug Protection */}
        <div className="bg-vextaris-dark/50 backdrop-blur-xl rounded-2xl border border-vextaris-purple/20 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-white flex items-center space-x-2">
              <ShieldCheckIcon className="w-6 h-6 text-green-400" />
              <span>Anti-Rug Protection</span>
            </h2>
            <div className="flex items-center space-x-2">
              <ChartBarIcon className="w-5 h-5 text-vextaris-lime" />
              <span className="text-sm font-medium text-gray-300">
                Safety Score: <span className={`${antiRugScore >= 75 ? 'text-green-400' : antiRugScore >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>{antiRugScore}/100</span>
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Max Wallet % (0 = no limit)
              </label>
              <input
                type="number"
                name="maxWalletPercent"
                value={formData.maxWalletPercent}
                onChange={handleChange}
                min="0"
                max="100"
                className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white focus:outline-none focus:border-vextaris-purple"
              />
              <p className="text-xs text-gray-500 mt-1">Prevents whale accumulation</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Max Transaction % (0 = no limit)
              </label>
              <input
                type="number"
                name="maxTxPercent"
                value={formData.maxTxPercent}
                onChange={handleChange}
                min="0"
                max="100"
                className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white focus:outline-none focus:border-vextaris-purple"
              />
              <p className="text-xs text-gray-500 mt-1">Prevents large dumps</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Liquidity Lock (days)
              </label>
              <input
                type="number"
                name="liquidityLockDays"
                value={formData.liquidityLockDays}
                onChange={handleChange}
                min="0"
                className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white focus:outline-none focus:border-vextaris-purple"
              />
              <p className="text-xs text-gray-500 mt-1">Recommended: 365+ days</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Dev Vesting (months)
              </label>
              <input
                type="number"
                name="devVestingMonths"
                value={formData.devVestingMonths}
                onChange={handleChange}
                min="0"
                className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white focus:outline-none focus:border-vextaris-purple"
              />
              <p className="text-xs text-gray-500 mt-1">Team token lockup period</p>
            </div>
          </div>
        </div>

        {/* Reputation Score Preview */}
        <div className="bg-vextaris-dark/50 backdrop-blur-xl rounded-2xl border border-vextaris-purple/20 p-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-semibold text-white flex items-center space-x-2">
              <ShieldCheckIcon className="w-6 h-6 text-vextaris-lime" />
              <span>Reputation Score Preview</span>
            </h2>
            <span className={`px-3 py-1 rounded-full text-sm font-bold border ${gradeColors[reputationPreview.grade]}`}>
              Grade {reputationPreview.grade} — {reputationPreview.score}/100
            </span>
          </div>
          <p className="text-sm text-gray-400 mb-4">
            This score is computed on-chain using VSC-REP and affects your token's visibility and trust.
          </p>

          <div className="space-y-3">
            {Object.entries(reputationPreview.factors).map(([key, factor]) => (
              <div key={key}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-300 capitalize">{key.replace(/_/g, ' ')}</span>
                  <span className="text-white font-medium">{factor.points}/{factor.max_points}</span>
                </div>
                <div className="w-full h-2 bg-vextaris-darker rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-vextaris rounded-full transition-all"
                    style={{ width: `${(factor.points / factor.max_points) * 100}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-0.5">{factor.reason}</p>
              </div>
            ))}
          </div>

          <div className="mt-4 p-3 rounded-lg bg-vextaris-purple/5 border border-vextaris-purple/10">
            <p className="text-xs text-gray-400">
              <span className="text-vextaris-lime font-medium">Pro Tip:</span> Lock metadata for +5 pts, renounce mint for +20 pts, lock LP 365+ days for +20 pts.
            </p>
          </div>
        </div>

        {/* Cost Summary */}
        <div className="bg-gradient-to-r from-vextaris-purple/10 to-vextaris-blue/10 rounded-2xl border border-vextaris-purple/30 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Estimated Cost</p>
              <p className="text-3xl font-bold text-white">{cost} VXS</p>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="px-8 py-3 bg-gradient-vextaris text-white rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : isConnected ? 'Create Token' : 'Connect Wallet to Create'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}